import Vue from "vue";
import { UIMessage, ChatStatus } from "../types";

interface ChatStreamOptions {
  apiUrl: string;
  executeUrl: string;
  getToken: () => string;
  // Called when the chat backend returns 401 (officer token expired). The
  // chat uses fetch() directly and so bypasses the axios 401 interceptor —
  // this callback is how the host app gets a chance to log the user out.
  onAuthError?: () => void;
  // Called when the chat backend returns 403 (officer is not on the
  // server-side allowlist — e.g. revoked mid-session). The host should hide
  // the chat UI; the actual gate is enforced server-side.
  onForbidden?: () => void;
}

interface ChatState {
  messages: UIMessage[];
  status: ChatStatus;
  pendingTool: null | {
    toolCallId: string;
    toolName: string;
    args: Record<string, unknown>;
  };
  error: string | null;
  trustedTools: string[];
}

export interface ChatController {
  state: ChatState;
  send: (userText: string) => Promise<void>;
  approveTool: () => Promise<void>;
  approveToolAndTrust: () => Promise<void>;
  rejectTool: () => void;
  retry: () => Promise<void>;
  reset: () => void;
  isTrusted: (toolName: string) => boolean;
}

// The conversation is kept in sessionStorage so it survives minimizing the
// panel, a page reload, or navigating between back-office pages — all within
// the same browser tab. It is intentionally NOT localStorage: a new tab or a
// new day starts fresh, and nothing officer-specific lingers after the tab
// closes.
const STORAGE_KEY = "khabeer-ai-chat";

function loadPersistedState(): {
  messages: UIMessage[];
  trustedTools: string[];
} | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages)) return null;
    return {
      messages: parsed.messages,
      trustedTools: Array.isArray(parsed.trustedTools)
        ? parsed.trustedTools
        : [],
    };
  } catch {
    return null;
  }
}

export function createChatStream(options: ChatStreamOptions): ChatController {
  const restored = loadPersistedState();
  const state = Vue.observable<ChatState>({
    messages: restored?.messages ?? [],
    // status/pendingTool/error are runtime-only — a restored conversation
    // always comes back idle, never mid-stream or mid-approval.
    status: "idle",
    pendingTool: null,
    error: null,
    trustedTools: restored?.trustedTools ?? [],
  });

  // Snapshot the conversation to sessionStorage. Called whenever messages
  // settle, so a reload/minimise/navigation picks up where the officer left.
  function persist() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          messages: state.messages,
          trustedTools: state.trustedTools,
        })
      );
    } catch {
      // sessionStorage unavailable or full — non-fatal, just skip persisting.
    }
  }
  // Remove a trailing assistant message left behind by a failed turn — one
  // with no real text and no tool call. Without this, every failed attempt
  // leaves an empty grey bubble that also accumulates on retry.
  function dropTrailingFailedAssistant() {
    const last = state.messages[state.messages.length - 1];
    if (!last || last.role !== "assistant") return;
    const hasContent = last.parts.some(
      (p) =>
        (p.type === "text" && p.text.trim().length > 0) ||
        p.type === "tool-invocation"
    );
    if (!hasContent) state.messages.pop();
  }

  async function send(userText: string) {
    if (state.status === "streaming") return;
    // The composer stays usable after an error so the officer can simply
    // type again instead of hunting for Reset — clean the failed bubble first.
    dropTrailingFailedAssistant();
    const userMsg: UIMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      parts: [{ type: "text", text: userText }],
    };
    state.messages.push(userMsg);
    state.status = "streaming";
    state.error = null;
    persist();
    await streamChat();
  }

  // Re-run the last turn after a failure (network error, backend 5xx, stream
  // error) without losing the conversation. Safe to call only when not
  // actively streaming.
  async function retry() {
    if (state.status === "streaming") return;
    if (!state.messages.length) return;
    state.error = null;
    dropTrailingFailedAssistant();
    state.status = "streaming";
    await streamChat();
  }

  // Re-run the last turn after a failure (network error, backend 5xx, stream
  // error) without losing the conversation. Safe to call only when not
  // actively streaming.
  async function retry() {
    if (state.status === "streaming") return;
    if (!state.messages.length) return;
    state.error = null;
    dropTrailingFailedAssistant();
    state.status = "streaming";
    await streamChat();
  }

  function updateToolInvocationInPlace(
    toolCallId: string,
    newState: "result" | "error",
    result: unknown
  ): boolean {
    for (const msg of state.messages) {
      for (const part of msg.parts) {
        if (
          part.type === "tool-invocation" &&
          part.toolInvocation.toolCallId === toolCallId
        ) {
          part.toolInvocation.state = newState;
          part.toolInvocation.result = result;
          return true;
        }
      }
    }
    return false;
  }

  function isTrusted(toolName: string): boolean {
    return state.trustedTools.indexOf(toolName) !== -1;
  }

  async function executePendingTool() {
    if (!state.pendingTool) return;
    const { toolCallId, toolName, args } = state.pendingTool;
    state.status = "streaming";
    try {
      const response = await fetch(options.executeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-OFFICER-TOKEN": options.getToken(),
        },
        body: JSON.stringify({ toolName, parameters: args }),
      });
      if (response.status === 401) {
        options.onAuthError?.();
        state.error = "Session expired";
        state.status = "error";
        state.pendingTool = null;
        return;
      }
      if (response.status === 403) {
        options.onForbidden?.();
        state.error = "Access denied";
        state.status = "error";
        state.pendingTool = null;
        return;
      }
      const data = await response.json();
      const isError = !response.ok || data?.result?.isError;

      // On an HTTP error from /execute-tool the real backend message is in
      // `data.error` — `data.result` is undefined. Without this normalisation
      // toWireMessages can't find `.content[0].text` and feeds the model the
      // literal string "tool error" with no detail. Coerce to the MCP content
      // shape so the actual error reaches the model verbatim.
      let resultPayload = data.result;
      if (
        isError &&
        !(
          resultPayload &&
          typeof resultPayload === "object" &&
          "content" in resultPayload
        )
      ) {
        const text =
          (typeof data?.error === "string" && data.error) ||
          resultPayload?.content?.[0]?.text ||
          `Tool execution failed (HTTP ${response.status})`;
        resultPayload = { content: [{ type: "text", text }], isError: true };
      }

      updateToolInvocationInPlace(
        toolCallId,
        isError ? "error" : "result",
        resultPayload
      );
      state.pendingTool = null;

      await streamChat();
    } catch (e: any) {
      state.error = e?.message ?? "Tool execution failed";
      state.status = "error";
      state.pendingTool = null;
    }
  }

  async function approveTool() {
    return executePendingTool();
  }

  async function approveToolAndTrust() {
    if (!state.pendingTool) return;
    const name = state.pendingTool.toolName;
    if (!isTrusted(name)) state.trustedTools = [...state.trustedTools, name];
    return executePendingTool();
  }

  function rejectTool() {
    if (!state.pendingTool) return;
    const { toolCallId } = state.pendingTool;
    updateToolInvocationInPlace(toolCallId, "error", {
      content: [{ type: "text", text: "User rejected this action." }],
      isError: true,
    });
    state.pendingTool = null;
    state.status = "idle";
    persist();
  }

  function reset() {
    state.messages = [];
    state.status = "idle";
    state.pendingTool = null;
    state.error = null;
    state.trustedTools = [];
    persist();
  }

  function toWireMessages(messages: UIMessage[]) {
    return messages
      .map((msg) => ({
        ...msg,
        parts: msg.parts
          // Drop empty/whitespace-only text parts — they would create
          // empty assistant ModelMessages that confuse tool_use/tool_result
          // pairing on Anthropic's side.
          .filter((part) => !(part.type === "text" && !part.text.trim()))
          .map((part) => {
            if (part.type !== "tool-invocation") return part;
            const inv = part.toolInvocation;

            // Drop any tool part that isn't fully resolved — Anthropic
            // rejects tool_use without tool_result. Covers:
            //  - state 'call' (never executed / waiting for approval that
            //    was never given and got stranded in a prior turn)
            //  - state 'result' but output is null/undefined (stream ended
            //    before tool-output-available arrived)
            if (inv.state === "call") return null;
            if (
              inv.state === "result" &&
              (inv.result === undefined || inv.result === null)
            )
              return null;

            const base: any = {
              type: "dynamic-tool",
              toolName: inv.toolName,
              toolCallId: inv.toolCallId,
              input: inv.args,
            };

            if (inv.state === "result") {
              base.state = "output-available";
              base.output = inv.result;
            } else {
              const errText =
                typeof inv.result === "object" &&
                inv.result &&
                "content" in (inv.result as any)
                  ? String(
                      (inv.result as any).content?.[0]?.text ?? "tool error"
                    )
                  : "tool error";
              base.state = "output-error";
              base.errorText = errText;
            }
            return base;
          })
          .filter((p): p is any => p !== null),
      }))
      .filter((m) => m.parts.length > 0);
  }

  async function streamChat() {
    let response: Response;
    try {
      response = await fetch(options.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-OFFICER-TOKEN": options.getToken(),
        },
        body: JSON.stringify({ messages: toWireMessages(state.messages) }),
      });
    } catch (e: any) {
      state.error = e?.message ?? "Network error";
      state.status = "error";
      return;
    }

    if (response.status === 401) {
      options.onAuthError?.();
      state.error = "Session expired";
      state.status = "error";
      return;
    }

    if (response.status === 403) {
      options.onForbidden?.();
      state.error = "Access denied";
      state.status = "error";
      return;
    }

    if (!response.ok || !response.body) {
      state.error = `Chat failed: ${response.status}`;
      state.status = "error";
      return;
    }

    // Do NOT pre-seed with an empty text part. Claude's multi-step loop
    // emits 'start-step' before any text, which would split [empty-text,
    // step-start, real-text] into two separate assistant ModelMessages —
    // an empty one and the real one. Anthropic then sees consecutive
    // assistant messages and can misroute tool_result indices. Start
    // empty; the first text-delta handler creates the text part.
    const assistantMsg: UIMessage = {
      id: `a-${Date.now()}`,
      role: "assistant",
      parts: [],
    };
    state.messages.push(assistantMsg);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        streamDone = true;
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) continue;
        const payload = line.startsWith("data:") ? line.slice(5).trim() : line;
        if (!payload || payload === "[DONE]") continue;
        try {
          const chunk = JSON.parse(payload);
          handleStreamChunk(chunk, assistantMsg);
        } catch {
          // non-JSON keepalive; ignore
        }
      }
    }

    if (state.pendingTool) {
      // If the officer previously opted-in to auto-approve this tool for the
      // rest of this session (e.g. a batch of per-store coupons), run it now
      // without showing the approval card.
      if (isTrusted(state.pendingTool.toolName)) {
        await executePendingTool();
        return;
      }
      state.status = "awaiting-approval";
    } else if (state.status !== "error") {
      state.status = "idle";
    }
    persist();
  }

  function handleStreamChunk(chunk: any, assistantMsg: UIMessage) {
    const type = chunk?.type;

    // Step boundaries — Claude's multi-step loop emits start-step at the
    // beginning of each reasoning/tool round-trip. We push a step-start UI
    // part so convertToModelMessages() splits the UIMessage into separate
    // assistant+tool ModelMessage pairs instead of emitting a malformed
    // [text, tool_use, text] single assistant message.
    if (type === "start-step") {
      assistantMsg.parts.push({ type: "step-start" });
      return;
    }

    if (type === "text-delta" && typeof chunk.delta === "string") {
      const last = assistantMsg.parts[assistantMsg.parts.length - 1];
      if (last && last.type === "text") {
        last.text += chunk.delta;
      } else {
        assistantMsg.parts.push({ type: "text", text: chunk.delta });
      }
      return;
    }

    if (type === "tool-input-available" && typeof chunk.toolName === "string") {
      const toolCallId = chunk.toolCallId;
      const toolName = chunk.toolName;
      const args = (chunk.input ?? {}) as Record<string, unknown>;

      assistantMsg.parts.push({
        type: "tool-invocation",
        toolInvocation: {
          toolCallId,
          toolName,
          args,
          state: "call",
        },
      });

      if (toolName.startsWith("action_")) {
        state.pendingTool = { toolCallId, toolName, args };
      }
      return;
    }

    if (
      type === "tool-output-available" &&
      typeof chunk.toolCallId === "string"
    ) {
      const part = assistantMsg.parts
        .filter((p) => p.type === "tool-invocation")
        .find(
          (p) =>
            p.type === "tool-invocation" &&
            p.toolInvocation.toolCallId === chunk.toolCallId
        );
      if (part && part.type === "tool-invocation") {
        const isErr = chunk?.output?.isError === true;
        part.toolInvocation.state = isErr ? "error" : "result";
        part.toolInvocation.result = chunk.output;
      }
      return;
    }

    if (type === "error") {
      state.error = String(chunk.errorText ?? chunk.error ?? "Unknown error");
      state.status = "error";
      return;
    }
  }

  return {
    state,
    send,
    approveTool,
    approveToolAndTrust,
    rejectTool,
    retry,
    reset,
    isTrusted,
  };
}
